import {PlusIcon, EnvelopeIcon, IdentificationIcon} from '@heroicons/react/24/outline'
import {FreeformComboBoxList} from '../../../../../../components/general/FreeformComboBoxList.tsx'
import AddressField, {AddressFieldValue} from '../../../../../../components/general/AddressField.tsx'
import {cn} from '../../../../../../lib/utils.ts'
import {fieldLabelClasses} from '../../../../../../components/general/fieldStyles.ts'
import {InvoiceCustomer} from '../types.ts'

type CustomerSectionProps = {
    customers: InvoiceCustomer[]
    selectedCustomer: InvoiceCustomer | null
    onSelectCustomer: (customer: InvoiceCustomer | null) => void
    address: AddressFieldValue
    onAddressChange: (address: AddressFieldValue) => void
    onOpenNewCustomer: () => void
    isLoadingCustomers: boolean
    customerError?: string
    addressError?: string
}

export function CustomerSection({
    customers,
    selectedCustomer,
    onSelectCustomer,
    address,
    onAddressChange,
    onOpenNewCustomer,
    isLoadingCustomers,
    customerError,
    addressError,
}: CustomerSectionProps) {

    type CustomerOption = ({displayName: string, id: string} & Partial<InvoiceCustomer>) | null

    function handleSelectCustomer(option: CustomerOption) {
        if (!option) {
            onSelectCustomer(null)
            return
        }

        const existing = customers.find(c => c.id === option.id)
        if (existing) {
            onSelectCustomer(existing)
            return
        }

        onSelectCustomer({
            id: option.id,
            name: option.displayName,
            email: '',
            ccEmail: '',
            vatNumber: '',
            addressExtraLine: '',
            address: null,
            defaultPaymentTerm: null,
            isAdHoc: true,
        })
    }

    return (
        <section>
            <h2 className="text-xl font-bold">Klant</h2>
            <p className="text-xs text-gray-600 mt-1 mb-4">
                Voor wie is deze factuur bedoeld? Kies een bestaande klant of voeg een nieuwe toe.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div id="customer-section" tabIndex={-1}>
                    <div className="flex items-center justify-between mb-1">
                        <label className={fieldLabelClasses}>Klant</label>
                        <button
                            type="button"
                            onClick={onOpenNewCustomer}
                            className="flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80"
                        >
                            <PlusIcon className="h-3.5 w-3.5"/>
                            Nieuwe klant
                        </button>
                    </div>
                    <FreeformComboBoxList<InvoiceCustomer>
                        label=""
                        emptySelectionMessage={isLoadingCustomers ? 'Klanten laden...' : 'Selecteer klant...'}
                        onSelect={handleSelectCustomer}
                        options={customers}
                        selected={selectedCustomer}
                        internalMapper={(item) => ({
                            ...item,
                            displayName: item.name,
                            id: item.id,
                        })}
                    />
                    {selectedCustomer?.email && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                            <EnvelopeIcon className="h-3.5 w-3.5"/>
                            {selectedCustomer.email}
                            {selectedCustomer.ccEmail && ` · CC ${selectedCustomer.ccEmail}`}
                        </p>
                    )}
                    {selectedCustomer?.vatNumber && (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                            <IdentificationIcon className="h-3.5 w-3.5"/>
                            {selectedCustomer.vatNumber}
                        </p>
                    )}
                    {selectedCustomer?.isAdHoc && (
                        <p className="mt-1.5 text-xs text-gray-500">
                            Deze naam staat nog niet in je klantenlijst. Voeg de klant toe om hem
                            de volgende keer te kunnen kiezen.
                        </p>
                    )}
                    {customerError && (
                        <p className="mt-1 text-xs text-red-600">{customerError}</p>
                    )}
                </div>
                <div id="address-container" tabIndex={-1} className="flex flex-col">
                    <label className={cn(fieldLabelClasses, 'mb-1')}>Klant adres</label>
                    <AddressField
                        label=""
                        value={address}
                        onChange={onAddressChange}
                        placeholder="Bijv. Damrak 1, 1012LG Amsterdam, NL"
                    />
                    {selectedCustomer?.addressExtraLine && (
                        <p className="mt-1.5 text-xs text-gray-500">{selectedCustomer.addressExtraLine}</p>
                    )}
                    {addressError && (
                        <p className="mt-1 text-xs text-red-600">{addressError}</p>
                    )}
                </div>
            </div>
        </section>
    )
}
